/**
 * Rebuy Smart Cart accessibility patches.
 *
 * The mini cart on this store is rendered by Rebuy (Vue) and its template lives in
 * the Rebuy admin, so accessibility fixes are applied from the theme after Rebuy
 * renders, and re-applied every time Rebuy re-renders.
 *
 * Rules for writing a patch (keeps Rebuy's Vue rendering intact):
 * - Only change attributes or insert NEW elements. Never move, wrap, unwrap or
 *   remove elements rendered by Rebuy: Vue patches its DOM in place and silently
 *   breaks (missing nodes, stale content) when nodes are moved. The single
 *   exception is the ADA-193 patch, which explains why it is safe there.
 * - Patches must be idempotent: they run on every DOM change inside the cart.
 * - Only write to the DOM when the value actually differs (see setAttr), so a
 *   patch never triggers itself through the MutationObserver.
 * - Wrap nothing in try/catch yourself: the runner isolates each patch so one
 *   failing patch does not stop the others.
 */
(function () {
  'use strict';

  const ROOT_SELECTOR = '#rebuy-cart';
  const STRINGS = {
    lineItemGroup: 'Product',
    recommendationGroup: 'Product',
    // Joins the visible button text and the product name: "Upgrade to Subscription, Night Shift".
    nameContextSeparator: ', ',
  };
  // Keeps an element in the accessibility tree (needs a layout box) without showing it.
  // display:block!important overrides Rebuy CSS that hides empty children of the line item.
  const VISUALLY_HIDDEN =
    'display:block!important;position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);border:0;';
  // Keeps the cart looking the same with an <h3> around the line item title: the theme's
  // heading styles are neutralised, the <h3> takes the full-width row the link had, and it
  // is a flex container so the link stays the block-level flex item Rebuy's CSS expects.
  const ITEM_HEADING_STYLE =
    'display:flex;width:100%;margin:0;padding:0;font:inherit;letter-spacing:inherit;text-transform:inherit;word-break:inherit;color:inherit;';

  let uid = 0;
  const ensureId = (el) => {
    if (!el.id) el.id = 'rebuy-a11y-' + ++uid;
    return el.id;
  };
  const setAttr = (el, name, value) => {
    if (el.getAttribute(name) !== value) el.setAttribute(name, value);
  };
  const removeAttr = (el, name) => {
    if (el.hasAttribute(name)) el.removeAttribute(name);
  };
  const normalize = (value) => (value || '').replace(/\s+/g, ' ').trim();
  // innerText is the rendered text, so it is empty for hidden elements and picks up
  // CSS text-transform. textContent keeps the casing as authored, which reads better
  // in an accessible name (WCAG 2.5.3 matching is case insensitive either way).
  const renderedText = (el) => normalize(el.innerText);
  const sourceText = (el) => normalize(el.textContent);
  const containsVisibleText = (name, visible) => normalize(name).toLowerCase().includes(visible.toLowerCase());
  const productNameFor = (item) => {
    const title = item.querySelector('.rebuy-cart__flyout-item-product-title');
    return title ? sourceText(title) : '';
  };

  const patches = [
    /**
     * ADA #156 - Remove the unnecessary role="group" Rebuy puts on the line item
     * list. The element is a <ul>, whose implicit list role is already correct,
     * so no replacement role is needed.
     */
    {
      id: 'ADA-156 line item list role',
      run(root) {
        const list = root.querySelector('.rebuy-cart__flyout-items');
        if (!list) return;
        removeAttr(list, 'role');
      },
    },

    /**
     * ADA #152 - Each line item is exposed as a labelled group so screen reader
     * users know where a card begins and ends.
     *
     * Rebuy renders <li aria-label="product">, putting the label on the list item
     * itself. We remove that label and insert a hidden
     * <div role="group" aria-label="Product"> that adopts the item's sections via
     * aria-owns. The sections stay where Vue rendered them, so the result in the
     * accessibility tree is list > listitem > group > sections.
     */
    {
      id: 'ADA-152 line item group',
      run(root) {
        const list = root.querySelector('.rebuy-cart__flyout-items');
        if (!list) return;

        list.querySelectorAll(':scope > .rebuy-cart__flyout-item').forEach((item) => {
          removeAttr(item, 'aria-label');

          let group = item.querySelector(':scope > .rebuy-a11y-line-item-group');
          if (!group) {
            group = document.createElement('div');
            group.className = 'rebuy-a11y-line-item-group';
            group.setAttribute('role', 'group');
            group.setAttribute('aria-label', STRINGS.lineItemGroup);
            group.style.cssText = VISUALLY_HIDDEN;
            item.insertBefore(group, item.firstChild);
          }

          const owned = Array.from(item.children)
            .filter((child) => child !== group)
            .map(ensureId)
            .join(' ');
          setAttr(group, 'aria-owns', owned);
        });
      },
    },
    /**
     * ADA #157 - WCAG 2.5.3 Label in Name: the accessible name of the line item
     * subscription button must contain its visible text.
     *
     * Rebuy labels it "Switch <product> to a Subscription" while it reads
     * "Upgrade to Subscription & Save 20%", so speech input users cannot activate
     * it by saying what they see. Dropping the aria-label would fix the mismatch
     * but leave every item's button named identically, so instead the name starts
     * with the visible text and keeps the product as trailing context.
     */
    {
      id: 'ADA-157 subscription button label in name',
      run(root) {
        root.querySelectorAll('.rebuy-cart__flyout-item-subscription button').forEach((button) => {
          const visible = renderedText(button);
          if (!visible) return; // no visible text: the aria-label is the only name, leave it alone

          const label = button.getAttribute('aria-label');
          if (label && containsVisibleText(label, visible)) return; // already compliant

          const item = button.closest('.rebuy-cart__flyout-item');
          const product = item ? productNameFor(item) : '';
          const name = sourceText(button);
          setAttr(button, 'aria-label', product ? name + STRINGS.nameContextSeparator + product : name);
        });
      },
    },
    /**
     * ADA #159 - Each recommendation card in the cart is exposed as a labelled
     * group so screen reader users know where a card begins and ends.
     *
     * Rebuy renders the cards as <div class="rebuy-product-block"
     * aria-label="product">. A plain div has no role, so the label is ignored and
     * the card boundary is never announced. Unlike the line items in ADA-152,
     * these wrappers are not list items, so the role goes straight on them.
     * Scoped to the whole cart, not just the recommendations container, because
     * Rebuy can also place widgets in the header, app and footer slots.
     */
    {
      id: 'ADA-159 recommendation card group',
      run(root) {
        root.querySelectorAll('.rebuy-product-block').forEach((card) => {
          setAttr(card, 'role', 'group');
          // Rebuy already labels the card "product"; only name it when it does not.
          if (!normalize(card.getAttribute('aria-label'))) {
            setAttr(card, 'aria-label', STRINGS.recommendationGroup);
          }
        });
      },
    },
    /**
     * ADA #162 - The actionable elements inside the cart product cards must stay
     * in the tab order.
     *
     * Rebuy renders the card image link with tabindex="-1", and the carousel adds
     * the same to the links and the Add button of the cards it treats as
     * off-screen, so everything past the first card is unreachable by keyboard.
     * Every card is reachable by scrolling the cart, so every action in one
     * belongs in the tab order.
     *
     * Only negative values are removed: tabindex="0" is often what puts an
     * element in the order to begin with, and a positive value would be a
     * separate bug to fix at the source.
     */
    {
      id: 'ADA-162 card actions in tab order',
      run(root) {
        root.querySelectorAll('.rebuy-product-block a[tabindex], .rebuy-product-block button[tabindex]').forEach((el) => {
          if (!(parseFloat(el.getAttribute('tabindex')) < 0)) return;
          // A card hidden from screen readers is meant to be out of the tab order too;
          // this also skips the whole cart while it is closed (aria-hidden on the root).
          if (el.closest('[aria-hidden="true"]')) return;
          removeAttr(el, 'tabindex');
        });
      },
    },
    /**
     * ADA #193 - The line item title must be marked up as a heading.
     *
     * Rebuy puts role="heading" aria-level="5" on the title link itself. The audit
     * asks for heading markup wrapped around the title and does not accept an
     * empty heading that adopts the link through aria-owns, so the link is moved
     * into a real <h3>. Level 3 sits under the cart's own <h2> title.
     *
     * This is the one exception to the "never wrap" rule at the top of this file,
     * and it holds only because of where the link sits: it is the first node Vue
     * renders inside .rebuy-cart__flyout-item-info and it is unconditional, so Vue
     * keeps patching it in place (text, href) and never needs it as the reference
     * node for inserting a sibling. If the link is ever not first, it is left
     * alone, with Rebuy's own heading role, rather than risk the cart. If Rebuy
     * replaces the link, the emptied <h3> is removed and the new link is wrapped
     * on the next run.
     */
    {
      id: 'ADA-193 line item title heading',
      run(root) {
        root.querySelectorAll('.rebuy-cart__flyout-item-product-title').forEach((title) => {
          const parent = title.parentElement;
          if (!parent) return;

          if (!parent.classList.contains('rebuy-a11y-item-heading')) {
            if (parent.firstElementChild !== title) return;

            const heading = document.createElement('h3');
            heading.className = 'rebuy-a11y-item-heading';
            heading.style.cssText = ITEM_HEADING_STYLE;
            parent.insertBefore(heading, title);
            heading.appendChild(title);
          }

          removeAttr(title, 'role');
          removeAttr(title, 'aria-level');
        });

        root.querySelectorAll('.rebuy-a11y-item-heading').forEach((heading) => {
          if (!heading.querySelector('.rebuy-cart__flyout-item-product-title')) heading.remove();
        });
      },
    },
  ];

  function runAll() {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root) return;
    patches.forEach((patch) => {
      try {
        patch.run(root);
      } catch (error) {
        console.warn('[Rebuy a11y] patch failed:', patch.id, error);
      }
    });
  }

  let scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      runAll();
    });
  }

  let started = false;
  function start() {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root) return false;
    if (started) return true;
    started = true;
    runAll();
    new MutationObserver(schedule).observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      // Attributes Rebuy may re-apply that patches need to re-fix. Extend when a patch relies on others.
      attributeFilter: ['role', 'aria-label', 'aria-hidden', 'tabindex'],
    });
    return true;
  }

  ['rebuy:smartcart.init', 'rebuy:smartcart.ready'].forEach((eventName) => {
    document.addEventListener(eventName, start);
  });

  if (!start()) {
    // Rebuy is loaded by a script tag after consent, so wait for #rebuy-cart to be added.
    const bodyObserver = new MutationObserver(() => {
      if (start()) bodyObserver.disconnect();
    });
    const observeBody = () => bodyObserver.observe(document.body, { childList: true, subtree: true });
    if (document.body) observeBody();
    else document.addEventListener('DOMContentLoaded', observeBody);
  }
})();
